/**
 * Generates a canvas-based result image for sharing.
 * Returns a Blob (PNG) or null if canvas is unavailable.
 */
export async function buildShareImage(
  xp: number,
  correctCount: number,
  totalQuestions: number,
  bestStreak: number,
  playerWon: boolean
): Promise<Blob | null> {
  try {
    const W = 600;
    const H = 320;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0f3460');
    bg.addColorStop(1, '#1a1a2e');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Gold accent bar at top
    ctx.fillStyle = '#f5a623';
    ctx.fillRect(0, 0, W, 4);

    // Title
    ctx.fillStyle = '#f5a623';
    ctx.font = 'bold 28px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Playing History', W / 2, 52);

    // Result line
    ctx.fillStyle = '#ffffff';
    ctx.font = '18px system-ui, sans-serif';
    ctx.fillText(playerWon ? '🏆 Victory!' : '😔 Defeat', W / 2, 88);

    // Stat boxes
    const stats = [
      { label: 'XP Earned', value: String(xp) },
      { label: 'Correct', value: `${correctCount}/${totalQuestions}` },
      { label: 'Best Streak', value: `🔥×${bestStreak}` },
      { label: 'Accuracy', value: `${Math.round((correctCount / Math.max(totalQuestions, 1)) * 100)}%` },
    ];

    const boxW = 120;
    const boxH = 80;
    const gap = 20;
    const totalRowW = stats.length * boxW + (stats.length - 1) * gap;
    const startX = (W - totalRowW) / 2;
    const boxY = 120;

    stats.forEach((s, i) => {
      const x = startX + i * (boxW + gap);
      // Box background
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      roundRect(ctx, x, boxY, boxW, boxH, 10);
      ctx.fill();
      // Value
      ctx.fillStyle = '#f5a623';
      ctx.font = 'bold 22px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(s.value, x + boxW / 2, boxY + 34);
      // Label
      ctx.fillStyle = '#90caf9';
      ctx.font = '12px system-ui, sans-serif';
      ctx.fillText(s.label, x + boxW / 2, boxY + 58);
    });

    // Footer
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('playing-history.eu · LT–BY history · Durak card game', W / 2, 260);

    // Gold border
    ctx.strokeStyle = 'rgba(245,166,35,0.4)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, W - 2, H - 2);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  } catch {
    return null;
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  w: number, h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
